import { useState } from "react";
import { useData } from "@/context/DataContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FileArrowDown, Image, TextAlignLeft, CheckCircle, Trash, BookOpen } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatTime, parseISO, todayISO, isoDate, startOfWeek } from "@/lib/date";

function monday(dateIso) {
  const s = startOfWeek(parseISO(dateIso));
  return isoDate(s);
}

export default function ImportPage() {
  const { categories, refresh } = useData();
  const [tab, setTab] = useState("text");
  const [weekStart, setWeekStart] = useState(monday(todayISO()));
  const [categoryId, setCategoryId] = useState("");
  const [text, setText] = useState("");
  const [ics, setIcs] = useState("");
  const [imageBase64, setImageBase64] = useState("");
  const [imagePreview, setImagePreview] = useState("");
  const [syllabus, setSyllabus] = useState("");
  const [syllabusItems, setSyllabusItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [proposed, setProposed] = useState([]);

  const parseText = async () => {
    if (!text.trim()) return toast.error("Plak eerst je rooster");
    setLoading(true);
    try {
      const { data } = await api.post("/import/text", { text, week_start: weekStart });
      const withCat = (data.proposed_events || []).map((e) => ({ ...e, category_id: categoryId || null }));
      setProposed(withCat);
      toast.success(`${withCat.length} afspraken herkend`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Herkennen mislukt");
    } finally { setLoading(false); }
  };

  const parseIcs = async () => {
    if (!ics.trim()) return toast.error("Plak .ics-inhoud of upload een bestand");
    setLoading(true);
    try {
      const { data } = await api.post("/import/ics", { ics_content: ics, default_category_id: categoryId || null });
      setProposed(data.proposed_events || []);
      toast.success(`${data.proposed_events?.length || 0} afspraken herkend`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Herkennen mislukt");
    } finally { setLoading(false); }
  };

  const onIcsFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setIcs(text);
  };

  const onImageFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setImagePreview(dataUrl);
      const commaIdx = dataUrl.indexOf(",");
      setImageBase64(commaIdx >= 0 ? dataUrl.slice(commaIdx + 1) : dataUrl);
    };
    reader.readAsDataURL(file);
  };

  const parseImage = async () => {
    if (!imageBase64) return toast.error("Upload eerst een afbeelding");
    setLoading(true);
    try {
      const { data } = await api.post("/import/image", {
        image_base64: imageBase64,
        week_start: weekStart,
        default_category_id: categoryId || null,
      });
      setProposed(data.proposed_events || []);
      toast.success(`${data.proposed_events?.length || 0} afspraken herkend`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Herkennen mislukt. Zorg dat het rooster duidelijk zichtbaar is.");
    } finally { setLoading(false); }
  };

  const parseSyllabus = async () => {
    if (!syllabus.trim()) return toast.error("Plak eerst een vakwijzer");
    setLoading(true);
    try {
      const { data } = await api.post("/import/syllabus", {
        text: syllabus,
        semester_start: weekStart,
      });
      const items = (data.proposed_items || []).map((it) => ({
        ...it,
        category_id: it.category_id || categoryId || null,
      }));
      setSyllabusItems(items);
      toast.success(`${items.length} items herkend`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Herkennen mislukt");
    } finally { setLoading(false); }
  };

  const updateSyllabusItem = (idx, patch) => {
    setSyllabusItems(syllabusItems.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };
  const removeSyllabusItem = (idx) => setSyllabusItems(syllabusItems.filter((_, i) => i !== idx));

  const saveSyllabus = async () => {
    if (syllabusItems.length === 0) return toast.error("Niets om op te slaan");
    setLoading(true);
    try {
      const { data } = await api.post("/import/syllabus/save", { items: syllabusItems });
      const total = (data.events || 0) + (data.tasks || 0) + (data.important || 0);
      toast.success(`${total} items geïmporteerd (${data.events} afspraken, ${data.tasks} taken, ${data.important} belangrijk)`);
      setSyllabusItems([]);
      setSyllabus("");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Opslaan mislukt");
    } finally { setLoading(false); }
  };

  const updateProposed = (idx, patch) => {
    setProposed(proposed.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  };

  const removeProposed = (idx) => setProposed(proposed.filter((_, i) => i !== idx));

  const saveAll = async () => {
    if (proposed.length === 0) return toast.error("Niets om op te slaan");
    setLoading(true);
    try {
      const { data } = await api.post("/import/save", { events: proposed });
      toast.success(`${data.count} afspraken geïmporteerd`);
      setProposed([]);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Opslaan mislukt");
    } finally { setLoading(false); }
  };

  return (
    <div className="p-5 lg:p-8 max-w-5xl">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-slate-500">Breng je leven binnen</div>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-50 mt-1">Rooster importeren</h1>
        <div className="text-sm text-slate-500 mt-1">Plak tekst, upload een .ics-bestand of een foto van je rooster. Nova haalt de afspraken er voor je uit.</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-500">Startweek (maandag)</Label>
          <Input
            type="date"
            value={weekStart}
            onChange={(e) => setWeekStart(e.target.value)}
            className="mt-1.5 bg-slate-900 border-slate-800 text-slate-100"
            data-testid="import-week-start"
          />
          <div className="text-[11px] text-slate-500 mt-1">Wekelijkse afspraken belanden op deze ma–zo en herhalen wekelijks.</div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-500">Standaardcategorie</Label>
          <Select value={categoryId} onValueChange={setCategoryId}>
            <SelectTrigger className="mt-1.5 bg-slate-900 border-slate-800 text-slate-100" data-testid="import-category">
              <SelectValue placeholder="Optioneel" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: c.color }} />{c.name}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger value="text" data-testid="import-tab-text"><TextAlignLeft size={14} className="mr-1.5" /> Tekst plakken</TabsTrigger>
          <TabsTrigger value="ics" data-testid="import-tab-ics"><FileArrowDown size={14} className="mr-1.5" /> .ics-bestand</TabsTrigger>
          <TabsTrigger value="image" data-testid="import-tab-image"><Image size={14} className="mr-1.5" /> Foto / PDF-pagina</TabsTrigger>
          <TabsTrigger value="syllabus" data-testid="import-tab-syllabus"><BookOpen size={14} className="mr-1.5" /> Vakwijzer</TabsTrigger>
        </TabsList>

        <TabsContent value="text" className="mt-4">
          <div className="rounded-xl border border-slate-800 bg-[#121C2F] p-5">
            <Label className="text-xs uppercase tracking-widest text-slate-500">Roostertekst</Label>
            <Textarea
              data-testid="import-text-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={"Voorbeeld:\nMa 09:00-10:00 Biologie (Lokaal 12)\nMa 11:00-12:30 Wiskunde\nDi 14:00-16:00 Natuurkunde practicum\n..."}
              className="mt-2 bg-slate-900 border-slate-800 text-slate-100 min-h-[220px] font-mono text-sm"
            />
            <div className="mt-4 flex justify-end">
              <Button data-testid="import-text-parse" disabled={loading} onClick={parseText} className="bg-blue-600 hover:bg-blue-500">
                {loading ? "Bezig..." : "Afspraken herkennen"}
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="ics" className="mt-4">
          <div className="rounded-xl border border-slate-800 bg-[#121C2F] p-5 space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Upload .ics-bestand</Label>
              <Input type="file" accept=".ics" onChange={onIcsFile} className="mt-1.5 bg-slate-900 border-slate-800 text-slate-100" data-testid="import-ics-file" />
            </div>
            <div className="text-center text-[11px] text-slate-500">of plak de inhoud hieronder</div>
            <Textarea
              value={ics}
              onChange={(e) => setIcs(e.target.value)}
              placeholder="BEGIN:VCALENDAR..."
              className="bg-slate-900 border-slate-800 text-slate-100 min-h-[140px] font-mono text-xs"
            />
            <div className="flex justify-end">
              <Button data-testid="import-ics-parse" disabled={loading} onClick={parseIcs} className="bg-blue-600 hover:bg-blue-500">
                {loading ? "Bezig..." : "Afspraken herkennen"}
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="image" className="mt-4">
          <div className="rounded-xl border border-slate-800 bg-[#121C2F] p-5 space-y-3">
            <Label className="text-xs uppercase tracking-widest text-slate-500">Rooster-afbeelding uploaden</Label>
            <Input type="file" accept="image/*" onChange={onImageFile} className="bg-slate-900 border-slate-800 text-slate-100" data-testid="import-image-file" />
            {imagePreview && (
              <div className="rounded-lg overflow-hidden border border-slate-800 max-h-72 bg-slate-950">
                <img src={imagePreview} alt="Preview" className="max-h-72 object-contain mx-auto" />
              </div>
            )}
            <div className="text-[11px] text-slate-500">Gebruikt AI om het rooster te lezen. Werkt het best met een helder rooster.</div>
            <div className="flex justify-end">
              <Button data-testid="import-image-parse" disabled={loading || !imageBase64} onClick={parseImage} className="bg-blue-600 hover:bg-blue-500">
                {loading ? "Analyseren..." : "Herkennen met AI"}
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="syllabus" className="mt-4">
          <div className="rounded-xl border border-slate-800 bg-[#121C2F] p-5 space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Plak je vakwijzer</Label>
              <div className="text-[11px] text-slate-500 mt-1">
                Nova vindt elke deadline, toets, opdracht en leesopdracht — en zet ze in je agenda & takenlijst.
              </div>
              <Textarea
                data-testid="import-syllabus-input"
                value={syllabus}
                onChange={(e) => setSyllabus(e.target.value)}
                placeholder={"Plak hier de complete vakwijzer, week-planning of opdrachtenlijst.\n\nVoorbeeld:\nWeek 1 (2 sep): Hoofdstuk 1 lezen; Toets 1 op 6 sep\nWeek 4 (23 sep): Midterm-tentamen\n15 okt: Essay-concept inleveren\n..."}
                className="mt-2 bg-slate-900 border-slate-800 text-slate-100 min-h-[240px] font-mono text-sm"
              />
            </div>
            <div className="flex justify-end">
              <Button
                data-testid="import-syllabus-parse"
                disabled={loading}
                onClick={parseSyllabus}
                className="bg-blue-600 hover:bg-blue-500"
              >
                {loading ? "Analyseren..." : "Alles herkennen"}
              </Button>
            </div>
          </div>

          {syllabusItems.length > 0 && (
            <div className="mt-4 rounded-xl border border-slate-800 bg-[#121C2F] p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">Voorbeeld</div>
                  <div className="text-slate-100 font-medium">
                    {syllabusItems.length} items · {syllabusItems.filter((i) => i.type === "important").length} important ·{" "}
                    {syllabusItems.filter((i) => i.type === "task").length} tasks ·{" "}
                    {syllabusItems.filter((i) => i.type === "event").length} events
                  </div>
                </div>
                <Button data-testid="import-syllabus-save" onClick={saveSyllabus} disabled={loading} className="bg-blue-600 hover:bg-blue-500 gap-2">
                  <CheckCircle size={16} /> Alles opslaan
                </Button>
              </div>
              <div className="space-y-2 max-h-[520px] overflow-y-auto">
                {syllabusItems.map((it, idx) => {
                  const tone = it.type === "important" ? "text-rose-300" : it.type === "task" ? "text-amber-300" : "text-blue-300";
                  return (
                    <div key={`${it.title}-${it.date}-${idx}`} data-testid={`syllabus-item-${idx}`} className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                      <div className={`md:col-span-1 text-[10px] uppercase tracking-widest ${tone}`}>{it.type}</div>
                      <Input value={it.title || ""} onChange={(e) => updateSyllabusItem(idx, { title: e.target.value })} className="md:col-span-5 bg-slate-900 border-slate-800 text-slate-100 h-8 text-sm" />
                      <Input type="date" value={it.date || ""} onChange={(e) => updateSyllabusItem(idx, { date: e.target.value })} className="md:col-span-2 bg-slate-900 border-slate-800 text-slate-100 h-8 text-sm" />
                      <div className="md:col-span-3 text-[11px] text-slate-500 truncate">
                        {it.important_kind && <span className="mr-2">· {it.important_kind}</span>}
                        {it.priority && <span className="mr-2">· {it.priority}</span>}
                        {it.notes && <span className="italic">{it.notes}</span>}
                      </div>
                      <button onClick={() => removeSyllabusItem(idx)} className="md:col-span-1 text-slate-500 hover:text-rose-400 justify-self-end"><Trash size={14} /></button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {proposed.length > 0 && (
        <div className="mt-6 rounded-xl border border-slate-800 bg-[#121C2F] p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500">Voorbeeld</div>
              <div className="text-slate-100 font-medium">{proposed.length} afspraken klaar om te importeren</div>
            </div>
            <Button data-testid="import-save" onClick={saveAll} disabled={loading} className="bg-blue-600 hover:bg-blue-500 gap-2">
              <CheckCircle size={16} /> Alles opslaan
            </Button>
          </div>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {proposed.map((ev, idx) => (
              <div key={`${ev.title}-${ev.date}-${ev.start_time}-${idx}`} data-testid={`import-preview-row-${idx}`} className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                <Input value={ev.title} onChange={(e) => updateProposed(idx, { title: e.target.value })} className="md:col-span-4 bg-slate-900 border-slate-800 text-slate-100 h-8 text-sm" />
                <Input type="date" value={ev.date} onChange={(e) => updateProposed(idx, { date: e.target.value })} className="md:col-span-2 bg-slate-900 border-slate-800 text-slate-100 h-8 text-sm" />
                <Input type="time" value={ev.start_time} onChange={(e) => updateProposed(idx, { start_time: e.target.value })} className="md:col-span-2 bg-slate-900 border-slate-800 text-slate-100 h-8 text-sm" />
                <Input type="time" value={ev.end_time} onChange={(e) => updateProposed(idx, { end_time: e.target.value })} className="md:col-span-2 bg-slate-900 border-slate-800 text-slate-100 h-8 text-sm" />
                <div className="md:col-span-2 flex items-center justify-between gap-2">
                  <span className="text-[10px] uppercase tracking-widest text-slate-500">{ev.recurrence || "none"}</span>
                  <button onClick={() => removeProposed(idx)} className="text-slate-500 hover:text-rose-400"><Trash size={14} /></button>
                </div>
                {ev.location && <div className="text-[11px] text-slate-500 md:col-span-12">📍 {ev.location}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
